import useTranslation from 'next-translate/useTranslation';
import { Children, ReactNode, useEffect, useRef, useState } from 'react';

import { Box, Icon, Image, Typography } from '@core/component';
import { Translate } from '@core/constant';
import { useContextHeaderHeight, useContextMediaQuery } from '@core/context';
import { getStaticPathIconFolder } from '@core/media';
import { EActionAttribute, EColor, EFontWeight, EIcon, EPosition, ESize } from '@core/type';

import { TableOfContentChildItem, TableOfContentItem } from './TableOfContentItem';
import {
  HEADER_OFFSET,
  HEADER_TOP_COMPENSATION,
  TABLE_OF_CONTENT_ID,
  TRANSITION_DELAY,
} from './constants';
import { TableContent } from './interface-table-of-content';

const getContainer = () => document.getElementById(TABLE_OF_CONTENT_ID);

export const TableOfContent = ({ children }: { children: ReactNode }) => {
  const { isMobile, mediaQuery } = useContextMediaQuery();
  const { height: headerHeight } = useContextHeaderHeight();
  const { t } = useTranslation(Translate.common.WIDGET);
  const contentRef = useRef<HTMLDivElement>(null);

  const [content, setContent] = useState<TableContent[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(!isMobile);
  const [height, setHeight] = useState<number | 'auto'>('auto');

  useEffect(() => {
    setHeight(contentRef?.current?.offsetHeight);

    if (isOpen) {
      setTimeout(() => {
        setHeight('auto');
      }, TRANSITION_DELAY);
    } else {
      setTimeout(() => {
        setHeight(0);
      }, 0);
    }
  }, [isOpen]);

  useEffect(() => {
    const container = getContainer();
    const parser = new DOMParser();

    const fragments = container.innerHTML
      .replaceAll('<h2', '___<h2')
      .split('___')
      .flatMap((fragment) => {
        const doc = parser.parseFromString(fragment, 'text/html');
        const h2 = doc.querySelector('h2');

        if (!h2) {
          return [];
        }

        const children: TableContent['children'] = [];
        const elements = doc.querySelectorAll('h3');

        elements.forEach((element) => {
          children.push({ title: element.textContent, isActive: false });
        });

        return [{ title: h2.textContent, children, isActive: false } as TableContent];
      });

    setContent(fragments);
  }, [children]);

  useEffect(() => {
    if (Children.toArray(children).length > 0) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  const handleScroll = () => {
    const container = getContainer();

    if (!container) {
      return;
    }

    const tags = Array.from(container.querySelectorAll('h2, h3'));

    tags.forEach((tag) => {
      const rect = tag.getBoundingClientRect();
      if (
        rect.top <= parseInt(headerHeight) + HEADER_OFFSET + HEADER_TOP_COMPENSATION &&
        rect.bottom >= parseInt(headerHeight)
      ) {
        setContent((prevState) =>
          prevState.map((h2) => {
            if (h2.title === tag.textContent) {
              return { ...h2, isActive: true };
            }

            if (h2.children.some((h3) => h3.title === tag.textContent)) {
              return {
                ...h2,
                isActive: true,
                children: h2.children.map((h3) => {
                  if (h3.title === tag.textContent) {
                    return { ...h3, isActive: true };
                  }

                  return { ...h3, isActive: false };
                }),
              };
            }

            return {
              ...h2,
              isActive: false,
              children: h2.children.map((h3) => ({ ...h3, isActive: false })),
            };
          }),
        );
      }
    });
  };

  const handleToggle = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleScrollToTitle = (text: string, tag: EActionAttribute.H2 | EActionAttribute.H3) => {
    const tags = Array.from(getContainer().querySelectorAll(tag));
    const currentTag = tags.find((tag) => tag.textContent === text);

    if (currentTag) {
      const rect = currentTag.getBoundingClientRect();

      window.scrollTo({
        top: rect.top + window.scrollY - parseInt(headerHeight) - HEADER_OFFSET,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box
      position={'sticky'}
      padding={'20px'}
      width={'100%'}
      maxHeight={
        isMobile ? undefined : `calc(100vh - ${mediaQuery === ESize.LG ? '230px' : '146px'})`
      }
      overflowY={isOpen ? 'auto' : 'hidden'}
      top={`calc(${headerHeight} + 24px)`}
      backgroundColor={{ semanticType: EColor.SECONDARY, variant: EColor.LIGHT }}
      borderRadius={'8px'}
      transition={'top 0.7s'}
    >
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        cursor={'pointer'}
        role={'button'}
        padding={'12px 16px'}
        onClick={handleToggle}
      >
        <Typography
          color={isOpen ? EColor.ACCENT : EColor.SECONDARY}
          fontWeight={EFontWeight.MEDIUM}
        >
          <Image
            src={getStaticPathIconFolder('charts/book-icon.svg')}
            alt="book-icon"
            width="16"
            height="16"
          />{' '}
          {t('tableOfContent.title')}
        </Typography>
        {!!content?.length && (
          <Box>
            <Icon type={EIcon.ANGLE} orientation={isOpen ? EPosition.TOP : EPosition.BOTTOM} />
          </Box>
        )}
      </Box>
      <Box
        as={EActionAttribute.UL}
        listStyleType={'none'}
        height={height}
        transition={'all 0.3s'}
        overflowY={'hidden'}
      >
        <Box ref={contentRef}>
          {content.map(({ title, children, isActive }, index) => (
            <TableOfContentItem
              key={`${index}/${title}`}
              isActive={isActive}
              title={title}
              onClick={() => handleScrollToTitle(title, EActionAttribute.H2)}
              isToggleable={children.length > 0}
            >
              {children.map(({ title: childTitle, isActive: isChildActive }, index) => (
                <TableOfContentChildItem
                  key={`${index}/${childTitle}`}
                  isActive={isChildActive}
                  title={childTitle}
                  onClick={() => handleScrollToTitle(childTitle, EActionAttribute.H3)}
                />
              ))}
            </TableOfContentItem>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
